import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ChartConfiguration } from 'chart.js';
import { Subscription, skip } from 'rxjs';

import { AlertService } from '../shared/alert/alert.service';
import { MonthService, monthShort } from '../shared/month/month.service';
import { ThemeService } from '../account/settings/services/theme.service';
import { cssColor } from '../../utils/theme-colors';
import { BarData, PIE_OPTIONS, PieData, applyChartTheme, brl } from '../finances/finance-charts';
import { FinanceRecords, FinanceSummaryService, summarizeFinanceMonth } from '../finances/finance-summary.service';
import { InvestmentRecords, InvestmentsSummaryService } from './dashboard/investments-summary.service';
import { InvestmentsDashboard, buildInvestmentsDashboard } from './dashboard/investments-dashboard';

// Dashboard of Investments: summary of the month, one card per screen and the charts of the old Overview
@Component({
  selector: 'app-investments',
  standalone: false,
  templateUrl: './investments.component.html',
  styleUrls: ['../shared/dashboard/dashboard.scss']
})
export class InvestmentsComponent implements OnInit, OnDestroy {
  dashboard: InvestmentsDashboard | null = null;
  isLoading = false;

  byClassChart: PieData | null = null;
  historyChart: BarData | null = null;
  pieOptions = PIE_OPTIONS;
  barOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: { callbacks: { label: ctx => `${ctx.dataset.label}: ${brl(ctx.parsed.y ?? 0)}` } }
    },
    scales: {
      x: { grid: { display: false } },
      y: { ticks: { callback: v => `R$ ${(Number(v) / 1000).toLocaleString('pt-BR')} mil` } }
    }
  };

  months = inject(MonthService);
  private investments = inject(InvestmentsSummaryService);
  private finances = inject(FinanceSummaryService);
  private themes = inject(ThemeService);
  private alert = inject(AlertService);
  private records: InvestmentRecords | null = null;
  private financeRecords: FinanceRecords | null = null;
  private subscriptions: Subscription[] = [];

  async ngOnInit(): Promise<void> {
    this.isLoading = true;
    try {
      // Read once; changing the month only recomputes
      [this.records, this.financeRecords] = await Promise.all([
        this.investments.load(),
        this.finances.load().catch(error => {
          console.error('InvestmentsComponent -> finances', error);
          return null;
        })
      ]);
    } catch (error) {
      console.error('InvestmentsComponent -> load', error);
      this.alert.show('Load failed!', 'error');
    } finally {
      this.isLoading = false;
    }

    this.subscriptions.push(
      this.months.month$.subscribe(() => this.build()),
      // Charts are drawn on canvas, so they are rebuilt with the colors of the new theme
      this.themes.dark$().pipe(skip(1)).subscribe(() => this.buildCharts())
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  // Net invested as a share of the balance left in Finances
  get balanceShare(): number | null {
    const summary = this.dashboard?.summary;
    return summary?.balance && summary.balance > 0 ? summary.totals.net / summary.balance : null;
  }

  get sellShares(): string {
    return [...new Set(this.dashboard?.summary.classes.flatMap(c => c.operations.filter(o => o.side == 'SELL').map(o => o.share)) ?? [])].join(', ');
  }

  private build(): void {
    if (!this.records) return;
    const month = this.months.month;
    const balance = this.financeRecords ? summarizeFinanceMonth(this.financeRecords, month).totals.balance : null;
    this.dashboard = buildInvestmentsDashboard(this.records, month, balance);
    this.buildCharts();
  }

  private buildCharts(): void {
    const dashboard = this.dashboard;
    if (!dashboard) return;
    applyChartTheme();

    const buys = dashboard.summary.classes.filter(c => c.buys > 0).sort((a, b) => b.buys - a.buys);
    this.byClassChart = buys.length ? {
      labels: buys.map(c => c.label),
      datasets: [{ data: buys.map(c => c.buys), backgroundColor: buys.map(c => cssColor(c.color)), borderColor: cssColor('var(--app-surface)') }]
    } : null;

    const history = dashboard.history;
    this.historyChart = history.some(h => h.buys || h.dividends) ? {
      labels: history.map(h => monthShort(h.month)),
      datasets: [
        { label: 'Aportes', data: history.map(h => h.buys), backgroundColor: cssColor('var(--app-bills)'), borderRadius: 3 },
        { label: 'Dividendos', data: history.map(h => h.dividends), backgroundColor: cssColor('var(--app-income)'), borderRadius: 3 }
      ]
    } : null;
  }
}
